/**
 * The artwork PDF a design renders. The print-ready proof embeds it.
 * This file is not the Final Print File.
 */

import { type ProofParts, proofFingerprint } from "./proof-fingerprint.ts";

/** A queued or building row that has not moved for this long is treated as failed. */
export const FINAL_PDF_STUCK_SECONDS = 15 * 60;

export const FINAL_PDF_STALE_MESSAGE =
  "Your design changed since this PDF was built. Create it again.";

export const FINAL_PDF_REQUIRED_MESSAGE = "Create the artwork PDF before approving.";

/** Step key stored on the row, and the sentence and bar the page shows. */
export const FINAL_PDF_STEPS = {
  queued: { step: "Waiting to start", progress: 10 },
  render: { step: "Rendering your design", progress: 40 },
  save: { step: "Saving the PDF", progress: 80 },
  ready: { step: "Your artwork PDF is ready", progress: 100 },
  failed: { step: "The build failed", progress: 0 },
} as const;

export type FinalPdfStep = keyof typeof FINAL_PDF_STEPS;

export type FinalPdfStatus = "none" | "queued" | "building" | "ready" | "failed";

export interface FinalPdfRecord {
  id: string;
  status: string;
  step: string | null;
  artwork_id: string | null;
  fingerprint: string | null;
  error: string | null;
  updated_at: number;
  ready_at: number | null;
  /** Set when they step back. Null while the file is the one on screen. */
  dismissed_at: number | null;
}

export interface FinalPdfView {
  id: string | null;
  status: FinalPdfStatus;
  step: string;
  progress: number;
  builtAt: number | null;
  artworkId: string | null;
  stale: boolean;
  /** Stepped back. The file is still stored. Create Proof shows it again. */
  dismissed: boolean;
  error: string | null;
}

const NONE: FinalPdfView = {
  id: null,
  status: "none",
  step: "",
  progress: 0,
  builtAt: null,
  artworkId: null,
  stale: false,
  dismissed: false,
  error: null,
};

/**
 * ponytail: a build that dies without writing `failed` is reported failed here,
 * from its last `updated_at`, instead of a sweeper. The ceiling is one row
 * left `building` until the next read. The upgrade is a queue dead-letter log.
 */
export function finalPdfStuck(status: string, updatedAt: number, nowSec: number): boolean {
  if (status !== "queued" && status !== "building") return false;
  return nowSec - updatedAt >= FINAL_PDF_STUCK_SECONDS;
}

/** Poll the campaign list while any row is still building. */
export function finalPdfPollMs(
  campaigns:
    | { final_pdf_status?: string | null; final_pdf_updated_at?: number | null }[]
    | undefined,
  nowSec = Math.floor(Date.now() / 1000),
): number | false {
  if (
    !campaigns?.some((c) => finalPdfBuilding(c.final_pdf_status, c.final_pdf_updated_at, nowSec))
  ) {
    return false;
  }
  return 5000;
}

/** True while the campaign list should show "Building PDF…" and keep polling. */
export function finalPdfBuilding(
  status: string | null | undefined,
  updatedAt: number | null | undefined,
  nowSec: number,
): boolean {
  if (status !== "queued" && status !== "building") return false;
  if (typeof updatedAt !== "number") return true;
  return !finalPdfStuck(status, updatedAt, nowSec);
}

function asStatus(status: string): FinalPdfStatus {
  switch (status) {
    case "queued":
    case "building":
    case "ready":
    case "failed":
      return status;
    default:
      return "failed";
  }
}

function stepOf(status: FinalPdfStatus, step: string | null): { step: string; progress: number } {
  if (status === "queued") return FINAL_PDF_STEPS.queued;
  if (status === "ready") return FINAL_PDF_STEPS.ready;
  if (status === "failed") return FINAL_PDF_STEPS.failed;
  if (step === "save") return FINAL_PDF_STEPS.save;
  if (step === "render" || step === "queued" || step == null) return FINAL_PDF_STEPS.render;
  return FINAL_PDF_STEPS.render;
}

/**
 * What the page polls. `currentFingerprint` is null until the row is ready;
 * a ready row whose fingerprint differs is stale.
 */
export function presentFinalPdf(
  row: FinalPdfRecord | null,
  nowSec: number,
  currentFingerprint: string | null,
): FinalPdfView {
  if (!row) return NONE;
  const timedOut = finalPdfStuck(row.status, row.updated_at, nowSec);
  const status = timedOut ? "failed" : asStatus(row.status);
  const shown = stepOf(status, row.step);
  const stale =
    status === "ready" && currentFingerprint != null && row.fingerprint !== currentFingerprint;
  return {
    id: row.id,
    status,
    step: timedOut ? "The build timed out. Try again." : shown.step,
    progress: timedOut ? 0 : shown.progress,
    builtAt: status === "ready" ? row.ready_at : null,
    artworkId: row.artwork_id,
    stale,
    dismissed: status === "ready" && !stale && row.dismissed_at != null,
    error: timedOut ? "The build timed out." : row.error,
  };
}

export type FinalPdfDisposition = "keep" | "restore" | "drop";

/**
 * A ready file that still matches is kept. A dismissed match is restored
 * instead of rebuilt. A mismatch is deleted.
 */
export function finalPdfDisposition(
  row: { status: string; fingerprint: string | null; dismissed_at: number | null },
  currentFingerprint: string | null,
): FinalPdfDisposition {
  if (row.status !== "ready" || currentFingerprint == null) return "keep";
  if (row.fingerprint !== currentFingerprint) return "drop";
  if (row.dismissed_at != null) return "restore";
  return "keep";
}

/** Bump when the unaddressed imprint on the print-ready PDF changes. */
export const UNADDRESSED_PDF_STAMP = 1;

/**
 * Identity of the file and the inputs that change what gets printed.
 * Addressed mail includes the personalisation fingerprint. Unaddressed mail
 * includes the postage stamp, so an older bare export no longer matches.
 */
export function finalPdfFingerprint(parts: {
  designId: string | null;
  designVersion: number | null;
  addressed: ProofParts | null;
  artworkId: string | null;
  artworkPages: string | null;
  artworkVersion: number;
}): string {
  const print = parts.addressed
    ? ["addressed", proofFingerprint(parts.addressed)]
    : [
        "unaddressed",
        UNADDRESSED_PDF_STAMP,
        parts.artworkId,
        parts.artworkPages,
        parts.artworkVersion,
      ];
  return JSON.stringify([parts.designId, parts.designVersion, print]);
}

export type ApproveAction = "generate" | "building" | "approve" | "retry";

/** Which button the approve screen shows. An upload is already a PDF, so it can be approved. */
export function approveAction(opts: {
  designId: string | null;
  finalPdf: { status: string; stale: boolean; dismissed?: boolean } | null;
}): ApproveAction {
  if (!opts.designId) return "approve";
  const pdf = opts.finalPdf;
  if (!pdf || pdf.status === "none") return "generate";
  switch (pdf.status) {
    case "queued":
    case "building":
      return "building";
    case "ready":
      return pdf.stale || pdf.dismissed ? "generate" : "approve";
    case "failed":
      return "retry";
    default:
      return "generate";
  }
}

export function approveActionLabel(action: ApproveAction): string {
  switch (action) {
    case "generate":
      return "Create Proof";
    case "building":
      return "Building PDF…";
    case "approve":
      return "Approve artwork";
    case "retry":
      return "Try again";
    default: {
      const unreachable: never = action;
      return unreachable;
    }
  }
}

/**
 * The confirm modal starts a new build. A ready file that still matches
 * is shown again with no modal, including after Make Changes.
 */
export function proofConfirmRequired(
  pdf: { status: FinalPdfStatus; stale: boolean } | null | undefined,
): boolean {
  if (!pdf) return true;
  switch (pdf.status) {
    case "none":
    case "failed":
      return true;
    case "ready":
      return pdf.stale;
    case "queued":
    case "building":
      return false;
    default: {
      const unreachable: never = pdf.status;
      return unreachable;
    }
  }
}

/**
 * Why this ready file cannot be approved, or null when it is the current one.
 * The route turns the message into a 409.
 */
export function finalPdfApprovalBlocker(opts: {
  status: string;
  artworkId: string | null;
  etag: string | null;
  fingerprint: string | null;
  campaignArtworkId: string | null;
  fileEtag: string | null;
  currentFingerprint: string;
}): string | null {
  if (opts.status !== "ready") return FINAL_PDF_STALE_MESSAGE;
  if (!opts.artworkId || opts.artworkId !== opts.campaignArtworkId) return FINAL_PDF_STALE_MESSAGE;
  if (!opts.etag || opts.etag !== opts.fileEtag) return FINAL_PDF_STALE_MESSAGE;
  if (!opts.fingerprint || opts.fingerprint !== opts.currentFingerprint) {
    return FINAL_PDF_STALE_MESSAGE;
  }
  return null;
}

/**
 * Why leaving this campaign should ask them to stay, or null when it should not.
 * They can still leave. A design with no ready file is the artwork PDF step.
 * An upload, or a design whose file is ready, is the approval step.
 * A file that is already building can be left; the ready notice still arrives.
 */
export function printReadyLeave(opts: {
  staff: boolean;
  hasArtwork: boolean;
  approvalStatus: string;
  needsPrintReady: boolean;
  building?: boolean;
}): string | null {
  if (opts.staff || !opts.hasArtwork || opts.approvalStatus !== "pending") return null;
  if (opts.building) return null;
  if (opts.needsPrintReady) {
    return "Create the artwork PDF before you leave this campaign. You can still leave.";
  }
  return "Check the print-ready proof before you leave this campaign. You can still leave.";
}

/** Edit Design, the design tool, and this campaign's page are not leaving. */
export function staysOnCampaign(pathname: string, campaignId: string): boolean {
  return (
    pathname.includes(`/mycampaigns/${campaignId}`) || pathname.includes(`/design/${campaignId}/`)
  );
}
